// 登录后强制改密 / 绑 2FA 守卫（改密优先）；代登录会话豁免（R86，后端已按 impersonatorAdminId 豁免强制门禁）
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { fetchMe } from '@/api/auth';
import { loadSession, saveSession, SESSION_CHANGED_EVENT } from '@/api/client';
import { isImpersonatedSession } from './impersonation';

/** 现网 App.tsx：settings/security 同时承载改密表单与 2FA */
const SETUP_PATH = '/merchant/settings/security';

function isSetupPath(pathname: string): boolean {
  return pathname.includes('password') || pathname.includes('security');
}

/** 订阅 session 写入，供布局/安全页在改密或绑 2FA 后重读标志 */
export function useSetupFlags() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((n) => n + 1);
    window.addEventListener(SESSION_CHANGED_EVENT, bump);
    return () => window.removeEventListener(SESSION_CHANGED_EVENT, bump);
  }, []);
  const session = loadSession();
  return {
    mustChangePassword: session?.me?.mustChangePassword === true,
    mfaSetupRequired: session?.me?.mfaSetupRequired === true,
    // 代登录会话标识：顶层 impersonated（portal-login 写入）优先，me.impersonated（契约 §0.4 R75）回落
    impersonated: isImpersonatedSession(session),
  };
}

export default function RequireSetup() {
  const location = useLocation();
  const { mustChangePassword, mfaSetupRequired, impersonated } = useSetupFlags();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchMe().then((res) => {
      if (cancelled) {
        return;
      }
      if (res.ok) {
        const s = loadSession();
        if (s) {
          saveSession({ ...s, me: res.data });
        }
      }
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return <div className="p-10 text-center text-sm text-slate-400">加载会话…</div>;
  }
  // R86：代登录会话不参与强制改密/绑 2FA 跳转（该页敏感动作已被 3432 硬禁，否则成单向死页）；
  // 普通会话判定逐字保留，门禁不放松。
  if (mustChangePassword && !impersonated && !isSetupPath(location.pathname)) {
    return <Navigate to={SETUP_PATH} replace={true} />;
  }
  if (mfaSetupRequired && !impersonated && !isSetupPath(location.pathname)) {
    return <Navigate to={SETUP_PATH} replace={true} />;
  }
  return <Outlet />;
}
